import { useState, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const useSicReporteGrupos = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reporteData, setReporteData] = useState(null);

    const generarReporte = useCallback(async (fechaDesde, fechaHasta) => {
        setLoading(true);
        setError(null);
        
        try {
            // Convertir las fechas al formato string que usa Firebase
            const fechaDesdeStr = formatearFechaParaFirebase(fechaDesde);
            const fechaHastaStr = formatearFechaParaFirebase(fechaHasta, true);
            
            console.log('Consultando Firebase con fechas:', { 
                fechaDesde: fechaDesdeStr, 
                fechaHasta: fechaHastaStr 
            });
            
            // 1. Consultar DetalleFactura en el rango de fechas (Compra y NC Compra)
            const detalleFacturaRef = collection(db, 'DetalleFactura');
            const q = query(
                detalleFacturaRef,
                where('fechaEmision', '>=', fechaDesdeStr),
                where('fechaEmision', '<=', fechaHastaStr),
                where('tipoFactura', 'in', ['Compra', 'NC Compra']), // Incluir NC Compra
                orderBy('fechaEmision')
            );

            const querySnapshot = await getDocs(q);
            console.log('Documentos encontrados:', querySnapshot.size);
            
            if (querySnapshot.size === 0) {
                setReporteData({});
                return;
            }

            // 2. Procesar los datos
            const datosProcesados = await procesarDatosFacturas(querySnapshot);
            console.log('Datos procesados:', datosProcesados);
            
            setReporteData(datosProcesados);
            
        } catch (err) {
            console.error('Error en generarReporte:', err);
            setError('Error al generar el reporte: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    return { generarReporte, reporteData, loading, error };
};

// Función para formatear fechas al formato de Firebase (string)
const formatearFechaParaFirebase = (fecha, esFinDeDia = false) => {
    const date = new Date(fecha);
    
    if (esFinDeDia) {
        date.setHours(23, 59, 59, 999);
    } else {
        date.setHours(0, 0, 0, 0);
    }
    
    // Formatear como '2025-08-18T11:27:25' (sin milisegundos y con timezone local)
    const año = date.getFullYear();
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const dia = String(date.getDate()).padStart(2, '0');
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    const segundos = String(date.getSeconds()).padStart(2, '0');
    
    return `${año}-${mes}-${dia}T${horas}:${minutos}:${segundos}`;
};

// Función para calcular promedio de forma segura (evita NaN)
const calcularPromedioSeguro = (valorActual, contador, nuevoValor) => {
    if (isNaN(valorActual)) valorActual = 0;
    if (isNaN(nuevoValor)) nuevoValor = 0;
    if (isNaN(contador)) contador = 0;
    
    if (contador === 0) return nuevoValor;
    
    return ((valorActual * contador) + nuevoValor) / (contador + 1);
};

// Función para obtener un número seguro (evita NaN)
const obtenerNumeroSeguro = (valor) => {
    const numero = Number(valor);
    return isNaN(numero) ? 0 : numero;
};

// Función para obtener el prefijo del código (primeros 6 dígitos)
const obtenerPrefijoGrupo = (codigoEmpresa) => {
    if (!codigoEmpresa || codigoEmpresa.length < 6) {
        return 'OTROS';
    }
    return codigoEmpresa.substring(0, 6);
};

// Función para obtener el nombre del grupo basado en el prefijo (genérico)
const obtenerNombreGrupo = (prefijo) => {
    return `Grupo ${prefijo}`;
};

// Función para procesar los datos de las facturas
const procesarDatosFacturas = async (querySnapshot) => {
    const datosPorProducto = {};
    const productosInfo = {};
    
    // Objeto para totales por semana
    const totalesPorSemana = {};

    console.log('Procesando', querySnapshot.size, 'documentos');

    // Primero, obtener información de productos desde SicDetalleFactura
    await obtenerInformacionProductos(productosInfo);
    console.log('Productos cargados:', productosInfo);

    // Procesar cada documento de DetalleFactura
    querySnapshot.forEach((doc) => {
        const factura = doc.data();
        console.log('Procesando factura:', factura);
        
        // Validar campos requeridos
        if (!validarFactura(factura)) {
            console.log('Factura no válida, saltando:', factura);
            return;
        }

        let fecha;
        if (typeof factura.fechaEmision === 'string') {
            fecha = new Date(factura.fechaEmision);
        } else if (factura.fechaEmision && factura.fechaEmision.toDate) {
            fecha = factura.fechaEmision.toDate();
        } else {
            console.log('Formato de fecha no reconocido:', factura.fechaEmision);
            return;
        }
        
        const semana = obtenerNumeroSemana(fecha);
        const mes = fecha.getMonth();
        const año = fecha.getFullYear();
        
        // Obtener información del producto
        const productoInfo = obtenerDetalleProducto(factura.codigoEmpresa, productosInfo);
        const nombreProducto = productoInfo?.detalle || `Código: ${factura.codigoEmpresa}`;
        
        // Obtener el grupo basado en los primeros 6 dígitos del código
        const prefijoGrupo = obtenerPrefijoGrupo(factura.codigoEmpresa);
        const nombreGrupo = obtenerNombreGrupo(prefijoGrupo);
        
        console.log(`Producto: ${nombreProducto}, Grupo: ${nombreGrupo}, Semana: ${semana}, Tipo: ${factura.tipoFactura}`);

        // Calcular montos en colones (con protección NaN)
        const montoTotal = calcularMontoEnColones(factura);
        const precioUnitario = calcularPrecioUnitarioEnColones(factura);
        
        // Determinar si es NC Compra (para restar en lugar de sumar)
        const esNCCompra = factura.tipoFactura === 'NC Compra';
        const factor = esNCCompra ? -1 : 1; // Multiplicador: -1 para NC Compra, 1 para Compra
        
        console.log(`Es NC Compra: ${esNCCompra}, Factor: ${factor}, Monto: ${montoTotal}`);
        
        // Inicializar estructura para el PRODUCTO si no existe
        if (!datosPorProducto[nombreProducto]) {
            datosPorProducto[nombreProducto] = {
                semanas: {},
                totales: {
                    cantidadTotal: 0,
                    montoTotal: 0
                },
                grupo: nombreGrupo,
                prefijo: prefijoGrupo,
                codigoEmpresa: factura.codigoEmpresa
            };
        }
        
        const productoData = datosPorProducto[nombreProducto];
        
        // Agregar datos por semana
        const claveSemana = `S${semana}_${mes}_${año}`;
        
        if (!productoData.semanas[claveSemana]) {
            productoData.semanas[claveSemana] = {
                precioUnitario: 0,
                cantidad: 0,
                monto: 0,
                contador: 0
            };
        }
        
        const semanaData = productoData.semanas[claveSemana];
        
        // Calcular promedio de precio unitario (con protección NaN)
        // Para NC Compra, usamos el valor absoluto para el promedio pero aplicamos factor al monto
        const precioUnitarioParaPromedio = esNCCompra ? Math.abs(precioUnitario) : precioUnitario;
        semanaData.precioUnitario = calcularPromedioSeguro(
            semanaData.precioUnitario, 
            semanaData.contador, 
            precioUnitarioParaPromedio
        );
        
        // Sumar cantidades y montos (con protección NaN) - aplicar factor para NC Compra
        const cantidadFactura = obtenerNumeroSeguro(factura.cantidad) * factor;
        const montoFactura = montoTotal * factor;
        
        semanaData.cantidad += cantidadFactura;
        semanaData.monto += montoFactura;
        semanaData.contador += 1;
        
        // Actualizar totales del producto (con protección NaN)
        productoData.totales.cantidadTotal += cantidadFactura;
        productoData.totales.montoTotal += montoFactura;
        
        // ACTUALIZAR TOTALES POR SEMANA
        if (!totalesPorSemana[claveSemana]) {
            totalesPorSemana[claveSemana] = {
                cantidadTotal: 0,
                montoTotal: 0,
                numero: semana,
                mes: mes,
                año: año
            };
        }
        
        totalesPorSemana[claveSemana].cantidadTotal += cantidadFactura;
        totalesPorSemana[claveSemana].montoTotal += montoFactura;
    });
    
    // Convertir a la estructura esperada por el componente (una sola tabla)
    return convertirEstructuraParaTabla(datosPorProducto, totalesPorSemana);
};

// Función para convertir a la estructura que espera la tabla (una sola tabla)
const convertirEstructuraParaTabla = (datosPorProducto, totalesPorSemana) => {
    const resultado = {
        "TODOS_LOS_PRODUCTOS": {
            productos: {},
            totales: {
                cantidadTotal: 0,
                montoTotal: 0,
                semanas: {}
            },
            totalesPorSemana: totalesPorSemana,
            grupos: {} // Para agrupar productos por grupo
        }
    };
    
    const empresa = resultado["TODOS_LOS_PRODUCTOS"];
    
    // Primero, agrupar productos por grupo
    Object.entries(datosPorProducto).forEach(([nombreProducto, datosProducto]) => {
        const grupo = datosProducto.grupo;
        
        if (!empresa.grupos[grupo]) {
            empresa.grupos[grupo] = {
                productos: {},
                totales: {
                    cantidadTotal: 0,
                    montoTotal: 0
                }
            };
        }
        
        // Agregar producto al grupo
        empresa.grupos[grupo].productos[nombreProducto] = datosProducto;
        empresa.grupos[grupo].totales.cantidadTotal += datosProducto.totales.cantidadTotal;
        empresa.grupos[grupo].totales.montoTotal += datosProducto.totales.montoTotal;
        
        // Agregar producto a la lista general (para mantener compatibilidad)
        empresa.productos[nombreProducto] = datosProducto;
        
        // Agregar a totales generales
        empresa.totales.cantidadTotal += datosProducto.totales.cantidadTotal;
        empresa.totales.montoTotal += datosProducto.totales.montoTotal;
    });
    
    // Consolidar semanas para la empresa
    Object.entries(totalesPorSemana).forEach(([claveSemana, datosSemana]) => {
        empresa.totales.semanas[claveSemana] = {
            numero: datosSemana.numero,
            mes: datosSemana.mes,
            año: datosSemana.año
        };
    });
    
    // Validar totales finales
    if (isNaN(empresa.totales.cantidadTotal)) empresa.totales.cantidadTotal = 0;
    if (isNaN(empresa.totales.montoTotal)) empresa.totales.montoTotal = 0;
    
    return resultado;
};

// Función para obtener información de productos
const obtenerInformacionProductos = async (productosInfo) => {
    try {
        const sicDetalleRef = collection(db, 'SicDetalleFactura');
        const querySnapshot = await getDocs(sicDetalleRef);
        
        querySnapshot.forEach(doc => {
            const data = doc.data();
            if (data.codigoComercialCodigo) {
                productosInfo[data.codigoComercialCodigo] = {
                    detalle: data.detalle || 'Sin descripción'
                };
            }
        });
    } catch (error) {
        console.error('Error al obtener información de productos:', error);
    }
};

// Función para obtener detalle del producto
const obtenerDetalleProducto = (codigoEmpresa, productosInfo) => {
    return productosInfo[codigoEmpresa] || null;
};

// Función para calcular montos en colones (con protección NaN)
const calcularMontoEnColones = (factura) => {
    let monto = obtenerNumeroSeguro(factura.montoTotalLinea);
    
    if (factura.codigoMoneda === 'USD' && factura.tipoCambio) {
        const tipoCambio = obtenerNumeroSeguro(factura.tipoCambio);
        monto *= tipoCambio;
    }
    
    return isNaN(monto) ? 0 : monto;
};

// Función para calcular precio unitario en colones (con protección NaN)
const calcularPrecioUnitarioEnColones = (factura) => {
    let precio = obtenerNumeroSeguro(factura.precioUnitario);
    
    if (factura.codigoMoneda === 'USD' && factura.tipoCambio) {
        const tipoCambio = obtenerNumeroSeguro(factura.tipoCambio);
        precio *= tipoCambio;
    }
    
    return isNaN(precio) ? 0 : precio;
};

// Función para obtener el número de semana
const obtenerNumeroSemana = (fecha) => {
    try {
        const firstDayOfYear = new Date(fecha.getFullYear(), 0, 1);
        const pastDaysOfYear = (fecha - firstDayOfYear) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    } catch (error) {
        console.error('Error calculando número de semana:', error);
        return 1;
    }
};

// Función para validar factura (mejorada)
const validarFactura = (factura) => {
    // Verificar que los campos numéricos no sean NaN
    const cantidadValida = !isNaN(obtenerNumeroSeguro(factura.cantidad));
    const precioUnitarioValido = !isNaN(obtenerNumeroSeguro(factura.precioUnitario));
    const montoTotalValido = !isNaN(obtenerNumeroSeguro(factura.montoTotalLinea));
    
    const isValid = factura.codigoEmpresa && 
           cantidadValida && 
           precioUnitarioValido &&
           montoTotalValido;
    
    if (!isValid) {
        console.log('Factura inválida - campos faltantes o inválidos:', {
            codigoEmpresa: factura.codigoEmpresa,
            cantidad: factura.cantidad,
            precioUnitario: factura.precioUnitario,
            montoTotalLinea: factura.montoTotalLinea,
            esValido: {
                cantidad: cantidadValida,
                precioUnitario: precioUnitarioValido,
                montoTotalLinea: montoTotalValido
            }
        });
    }
    
    return isValid;
};

export default useSicReporteGrupos;